'use client';

import { defineMessages, useIntl } from 'react-intl';

import { Typography } from '@packages/shared/src/components/Typography/Typography';
import { Box } from '@packages/shared/src/components/Box/Box';
import { LoginButton } from '@packages/shared/src/components/Button/LoginButton';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';

import type { SlotProps } from '../Base/Slot.client';
import { Slot } from '../Base/Slot.client';
import { LoginDialogComposed } from '../../../../LoginDialog/LoginDialogComposed/LoginDialogComposed';
import { useWishlistLink } from '../../shared/useWishlistLink';

// Messages for popover content only - the title is now handled by the hook
const messages = defineMessages({
  overlayHeadline: {
    id: 'slot.wishlist.overlay.headline',
    defaultMessage: 'Du vermisst Artikel, die du dir gemerkt hast?',
  },
  overlayContent: {
    id: 'slot.wishlist.overlay.content',
    defaultMessage: 'Dann melde dich bitte an, um deine gespeicherten Artikel zu sehen.',
  },
});

const DesktopPopoverContent = ({ onLogin }: { onLogin: () => void }) => {
  const { formatMessage } = useIntl();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, pt: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {formatMessage(messages.overlayHeadline)}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {formatMessage(messages.overlayContent)}
      </Typography>
      <Box sx={{ alignSelf: 'flex-end' }}>
        <LoginButton className="w-full" onClick={onLogin} />
      </Box>
    </Box>
  );
};

/** Wishlist slot with count badge and login popover. On mobile devices the slot is a link and has no popover */
export const WishlistSlot = (props: Pick<SlotProps, 'priority'>) => {
  const { language } = useI18n();
  const { isMobile } = useDeviceType();
  const { loggedIn } = useLoginState();
  const { lifeSupportForBasicallyDeadTenant } = useConfig();
  const { href, label, badgeCount } = useWishlistLink();

  return (
    <LoginDialogComposed>
      {(openLoginDialog) => (
        <Slot
          {...props}
          language={language}
          icon={<span className="icon-emp-wishlist size-4" />}
          badgeCount={badgeCount}
          title={label}
          href={href}
        >
          {!isMobile && !loggedIn && badgeCount === 0 && !lifeSupportForBasicallyDeadTenant && (
            <DesktopPopoverContent onLogin={openLoginDialog} />
          )}
        </Slot>
      )}
    </LoginDialogComposed>
  );
};
